import MasterUserStructure from "../Constants/MasterUserStructure"; // adjust path

/**
 * Fetches user data from the mock API endpoint
 * @returns {Promise<Object|null>} Formatted user data or null on error
 */
export const FetchMasterUserData = async () => {
  try {
    const response = await fetch("/api/mock/AccountPage/UserMasterDetailedInfo.json");
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Merge with MasterUserStructure to ensure all fields exist
    const formattedData = {
      ...MasterUserStructure,
      userId: data.userId || "",
      name: {
        firstName: data.name?.firstName || "",
        lastName: data.name?.lastName || ""
      },
      imageUrl: data.imageUrl || "",
      gender: data.gender || "",
      ethenic: data.ethenic || "",
      email: data.email || "",
      password: data.password || "",
      mobileNumber: data.mobileNumber || "",
      devices: Array.isArray(data.devices) ? data.devices : [],
      dateOfBirth: data.dateOfBirth || "",
      address: {
        street: data.address?.street || "",
        city: data.address?.city || "",
        state: data.address?.state || "",
        zipCode: data.address?.zipCode || "",
        country: data.address?.country || ""
      },
      isProfileSet: data.isProfileSet ?? false,
      nProfiles: data.nProfiles || 0,
      isCommunitySet: data.isCommunitySet ?? false,
      isKlamitySet: data.isKlamitySet ?? false,
      occupation: data.occupation || "",
      isVerified: data.isVerified ?? false,
      isBlueTickEnabled: data.isBlueTickEnabled ?? false,
      isOotshavSet: data.isOotshavSet ?? false,
      isDeluzonSet: data.isDeluzonSet ?? false,
      credibilityPoints: data.credibilityPoints || 0,
      isAdvertiser: data.isAdvertiser ?? false,
      isAdmin: data.isAdmin ?? false
    };

    return formattedData;
  } catch (error) {
    console.error("Failed to fetch user detailed info:", error);
    return null;
  }
};

/**
 * Fetches user data with error boundary
 * @returns {Promise<Object>} Formatted user data or default structure
 */
export const FetchUserDataSafe = async () => {
  const userData = await FetchMasterUserData();
  return userData || { ...MasterUserStructure };
};

/**
 * Validates user data structure
 * @param {Object} userData - User data to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const ValidateUserData = (userData) => {
  if (!userData) return false;
  
  const requiredFields = ['userId', 'email', 'name'];
  const hasRequiredFields = requiredFields.every(field => {
    if (field === 'name') {
      return userData.name?.firstName && userData.name?.lastName;
    }
    return userData[field];
  });
  
  return hasRequiredFields;
};

/**
 * Formats user display name
 * @param {Object} userData - User data object
 * @returns {string} Formatted display name
 */
export const GetUserDisplayName = (userData) => {
  if (!userData?.name) return "Unknown User";
  const { firstName, lastName } = userData.name;
  return `${firstName} ${lastName}`.trim() || "Unknown User";
};

/**
 * Checks if user has completed profile setup
 * @param {Object} userData - User data object
 * @returns {boolean} True if profile is complete
 */
export const IsProfileComplete = (userData) => {
  if (!userData) return false;
  
  return (
    userData.isProfileSet &&
    userData.nProfiles > 0 &&
    userData.email &&
    userData.mobileNumber &&
    userData.dateOfBirth
  );
};

/**
 * Gets user verification status
 * @param {Object} userData - User data object
 * @returns {Object} Verification status details
 */
export const GetVerificationStatus = (userData) => {
  if (!userData) {
    return {
      isVerified: false,
      hasBlueCheck: false,
      credibilityLevel: "none"
    };
  }
  
  const credibilityLevel = 
    userData.credibilityPoints >= 1000 ? "high" :
    userData.credibilityPoints >= 500 ? "medium" :
    userData.credibilityPoints > 0 ? "low" : "none";
  
  return {
    isVerified: userData.isVerified,
    hasBlueCheck: userData.isBlueTickEnabled,
    credibilityLevel,
    credibilityPoints: userData.credibilityPoints
  };
};

export default FetchMasterUserData;